import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL, AVAILABILITY } from "@/lib/content";

const initialForm = { name: "", phone: "", vehicle: "", preferred_time: "", notes: "" };

export const BookingSection = ({ selectedPackage, setSelectedPackage }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, package: selectedPackage });
      toast.success("Booking request sent! We'll text you shortly to confirm.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "h-12 rounded-xl border-[#E5E7EB] bg-white text-[#111827] placeholder:text-[#9CA3AF] focus-visible:ring-[#D97706]";

  return (
    <section id="book" className="border-t border-[#E5E7EB] bg-white py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl gap-14 px-5 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono-alt text-xs font-semibold uppercase tracking-[0.2em] text-[#B45309]">
            04 — Book now
          </p>
          <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-[#111827] sm:text-3xl lg:text-4xl">
            Book your detail in 30 seconds.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-[#4B5563]">
            Tell us what you drive and which clean you want. We'll text you back to lock in a time and place.
            Prefer to talk? Call us at{" "}
            <a href={PHONE_TEL} className="font-semibold text-[#D97706] hover:underline">
              {PHONE_DISPLAY}
            </a>
            .
          </p>

          <div
            data-testid="availability-card"
            className="mt-8 max-w-md rounded-2xl border border-[#E5E7EB] bg-[#FAF9F5] p-6"
          >
            <div className="flex items-center gap-2">
              <Clock size={15} className="text-[#D97706]" />
              <p className="font-mono-alt text-[10px] font-semibold tracking-[0.25em] text-[#B45309]">
                OUR AVAILABILITY
              </p>
            </div>
            <ul className="mt-4 space-y-2.5">
              {AVAILABILITY.map((a) => (
                <li key={a.days} className="flex items-baseline justify-between gap-4 text-sm">
                  <span className="font-semibold text-[#111827]">{a.days}</span>
                  <span className="text-[#4B5563]">{a.hours}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-[#9CA3AF]">
              We come to you during these windows — pick what works and we'll confirm by text.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-[#E5E7EB] bg-[#FAF9F5] p-8 sm:p-10"
            data-testid="booking-form"
          >
            <div className="space-y-5">
              <div>
                <Label htmlFor="bk-name" className="text-sm font-medium text-[#374151]">Full name</Label>
                <Input
                  id="bk-name"
                  data-testid="booking-form-name-input"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jordan Smith"
                  className={`mt-2 ${inputClass}`}
                />
              </div>
              <div>
                <Label htmlFor="bk-phone" className="text-sm font-medium text-[#374151]">Phone number</Label>
                <Input
                  id="bk-phone"
                  data-testid="booking-form-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="405-555-0123"
                  className={`mt-2 ${inputClass}`}
                />
              </div>
              <div>
                <Label htmlFor="bk-vehicle" className="text-sm font-medium text-[#374151]">Vehicle make & model</Label>
                <Input
                  id="bk-vehicle"
                  data-testid="booking-form-vehicle-input"
                  required
                  value={form.vehicle}
                  onChange={update("vehicle")}
                  placeholder="2019 Honda Civic"
                  className={`mt-2 ${inputClass}`}
                />
              </div>
              <div>
                <Label htmlFor="bk-package" className="text-sm font-medium text-[#374151]">Package</Label>
                <select
                  id="bk-package"
                  data-testid="booking-form-package-select"
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="mt-2 h-12 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                >
                  <option value="basic">Basic Clean — $60</option>
                  <option value="premium">Premium Clean — $80</option>
                </select>
              </div>
              <div>
                <Label htmlFor="bk-time" className="text-sm font-medium text-[#374151]">Preferred date & time</Label>
                <Input
                  id="bk-time"
                  data-testid="booking-form-date-input"
                  value={form.preferred_time}
                  onChange={update("preferred_time")}
                  placeholder="e.g. Saturday morning, or Wednesday after 4:30"
                  className={`mt-2 ${inputClass}`}
                />
              </div>
              <div>
                <Label htmlFor="bk-notes" className="text-sm font-medium text-[#374151]">Notes (optional)</Label>
                <Textarea
                  id="bk-notes"
                  data-testid="booking-form-notes-input"
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="Pet hair, stains, gate code — anything we should know."
                  className="mt-2 min-h-[90px] rounded-xl border-[#E5E7EB] bg-white text-[#111827] placeholder:text-[#9CA3AF] focus-visible:ring-[#D97706]"
                />
              </div>
              <button
                data-testid="booking-form-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-[#D97706] py-4 text-sm font-semibold text-white transition-colors hover:bg-[#B45309] disabled:opacity-60"
              >
                {submitting ? "Sending..." : `Request Booking — ${selectedPackage === "premium" ? "$80 Premium" : "$60 Basic"}`}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
